import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import React, { ChangeEvent, Dispatch, SetStateAction, useMemo, useState } from 'react';
import VoxelAlert from '../../../../components/voxel/Alert/VoxelAlert';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionButton from '../../../../components/voxel/Dialogs/VoxelDialogActionButton';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelInput from '../../../../components/voxel/Forms/VoxelInput';
import VoxelTextarea from '../../../../components/voxel/Forms/VoxelTextArea';
import VoxelInfo from '../../../../components/voxel/Info/VoxelInfo';
import VoxelTable from '../../../../components/voxel/Tables/VoxelTable';
import VoxelTableCell from '../../../../components/voxel/Tables/VoxelTableCell';
import VoxelTableHeadCell from '../../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTableRow from '../../../../components/voxel/Tables/VoxelTableRow';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import { TagGroupGq } from '../../../../graphql/projects/models/TagGroupGq';
import { NewTagGq } from '../../../../graphql/projects/mutations/tags/useAddTagsGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';

export interface AddTagsDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  tagGroup: TagGroupGq;
  onSuccess: () => Promise<any>;
}

interface FormState {
  tagGroupId: number;
  pasteArea: string;
  newTagValues: NewTagGq[];
}

const AddTagsDialog: React.FC<AddTagsDialogProps> = ({
  openDialog,
  setOpenDialog,
  tagGroup,
  onSuccess,
}) => {
  const [step, setStep] = useState<number>(1);

  const tagLimit = 100;
  const addTagsMutation = projectsApiService.mutations.tags.useAddTags();
  const formBag = useFormik<FormState>({
    initialValues: {
      tagGroupId: tagGroup.id,
      pasteArea: '',
      newTagValues: [],
    },
    onSubmit: (values, actions) => {
      addTagsMutation({
        projectId: tagGroup.projectId,
        tagGroupId: values.tagGroupId,
        tags: values.newTagValues,
      }).then(() => onSuccess());
    },
  });

  var tableTagsArray: NewTagGq[] = useMemo(() => {
    const tagList = formBag.values.pasteArea.trim().split('\n');
    const tagAndVariantsList: NewTagGq[] = tagList.map((t, index) => ({
      name: t.replaceAll(/\t+/g, ', ').trim().split(',')[0],
      textMatchVariants: t.replaceAll(/\t+/g, ', ').trim(),
    }));
    formBag.setFieldValue('newTagValues', tagAndVariantsList);
    return tagAndVariantsList;
  }, [formBag.values.pasteArea]);

  const currentNrTags = useMemo(
    () => tagGroup.tags.length + tableTagsArray.length,
    [tableTagsArray, tagGroup.tags]
  );

  const tooManyTags = useMemo(
    () => currentNrTags > tagLimit,
    [formBag.values.newTagValues, tagGroup.tags]
  );

  const errorText: string = useMemo(
    () =>
      'You have entered too many tags. Max number is ' +
      tagLimit +
      '. Current number is ' +
      currentNrTags +
      '.',
    [currentNrTags]
  );

  const editNewTagName = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const next = [...formBag.values.newTagValues];
    next[index] = { ...next[index], name: event.target.value };
    formBag.setFieldValue('newTagValues', next);
  };

  const editNewTagTextMatchVariant = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const next = [...formBag.values.newTagValues];
    next[index] = { ...next[index], textMatchVariants: event.target.value };
    formBag.setFieldValue('newTagValues', next);
  };
  const [showPreviousWarning, setShowPreviousWarning] = useState<boolean>(false);

  return (
    <VoxelDialog
      minWClass={'min-w-[1000px]'}
      title={'Add tags'}
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      closeOnBackdrop={false}
    >
      {step === 1 && (
        <VoxelDialogContent classMinWidth="min-w-full flex flex-col gap-4">
          <div>
            <VoxelText variant="bread-normal" paddingBot>
              {' '}
              Enter list of tags separated by line break (max {tagLimit} tags per tag group). Values
              separated by either comma or tab will be added as text match variants.{' '}
            </VoxelText>
            <div className="w-full flex flex-col gap-2">
              <VoxelTextarea
                minRows={18}
                resizeOff
                placeholder={
                  'tag 1, text match variant A, text match variant B...\ntag 2\t text match variant A \ttext match variant B...'
                }
                value={formBag.values.pasteArea}
                onChange={(event) => formBag.setFieldValue('pasteArea', event.target.value)}
                helperText={errorText}
                showHelperText={tooManyTags}
              />
            </div>
          </div>
        </VoxelDialogContent>
      )}
      {step === 1 && (
        <VoxelDialogActions>
          <VoxelDialogActionButton
            title="Continue"
            endIcon={<FontAwesomeIcon icon={faChevronRight} />}
            color="gray"
            onClick={() => setStep(step + 1)}
            disabled={
              !formBag.values.pasteArea ||
              formBag.isSubmitting ||
              !formBag.dirty ||
              !formBag.isValid ||
              tooManyTags
            }
          />
        </VoxelDialogActions>
      )}
      {step === 2 && (
        <VoxelDialogContent>
          <div>
            <VoxelText variant="bread-normal" paddingBot>
              View input as table and make final adjustments.
            </VoxelText>
            <div className="w-full max-h-96 overflow-y-auto block overscroll-contain">
              {formBag.values.pasteArea && (
                <VoxelTable className="w-full table-fixed">
                  <colgroup>
                    <col className="w-[40px]" />
                    <col className="w-6/12" />
                    <col className="w-7/12" />
                  </colgroup>
                  <thead className="sticky top-0 bg-white z-10">
                    <VoxelTableHeadCell>Nr</VoxelTableHeadCell>
                    <VoxelTableHeadCell size="md" className="pl-0">
                      Tag
                    </VoxelTableHeadCell>
                    <VoxelTableHeadCell size="md" className="pl-0">
                      Text match variants
                      <VoxelInfo
                        className="ml-1"
                        tooltip="Text match variants are added for the purpose of the autotag feature. Use comma (,) as separator if a tag should have multiple text match variants."
                      />
                    </VoxelTableHeadCell>
                  </thead>
                  <tbody className="h-50">
                    {tableTagsArray.map((newTag, index) => (
                      <VoxelTableRow key={newTag.name + ': ' + index} className="border-none">
                        <VoxelTableCell>{index + 1 + tagGroup.tags.length}</VoxelTableCell>
                        <VoxelTableCell size="md" className="pl-0 py-1">
                          <VoxelInput
                            inputWidthClass="w-full"
                            value={formBag.values.newTagValues[index]?.name}
                            onChange={(event) => editNewTagName(event, index)}
                          />
                        </VoxelTableCell>
                        <VoxelTableCell size="md" className="pl-0 py-1">
                          <VoxelInput
                            inputWidthClass="w-full"
                            value={formBag.values.newTagValues[index]?.textMatchVariants}
                            onChange={(event) => editNewTagTextMatchVariant(event, index)}
                          />
                        </VoxelTableCell>
                      </VoxelTableRow>
                    ))}
                  </tbody>
                </VoxelTable>
              )}
            </div>
          </div>
        </VoxelDialogContent>
      )}
      {step === 2 && (
        <div className="flex flex-row justify-between items-center">
          <VoxelDialogActionButton
            className="mx-8 mb-5"
            title="Previous"
            startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
            onClick={() => {
              setStep(step - 1);
              setShowPreviousWarning(true);
            }}
            color="gray"
          />
          <VoxelDialogActions>
            <VoxelDialogActionSubmit
              title="Submit"
              onClick={formBag.submitForm}
              disabled={formBag.isSubmitting || !formBag.dirty || !formBag.isValid || tooManyTags}
            />
          </VoxelDialogActions>
        </div>
      )}
      {step === 1 && showPreviousWarning && (
        <VoxelAlert severity="warning" size="sm" iconSize="lg">
          Changes done to the next step will be lost if you edit the current step.
        </VoxelAlert>
      )}
    </VoxelDialog>
  );
};

export default AddTagsDialog;
